import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getManageableTeams,
  bulkDeleteTeams,
  getDeletedTeams,
  restoreTeams,
} from "../../../../services/teams";
import { formatDateForDisplay } from "../../../../utils/formatters";
import { Button } from "../../../../components/Button";
import { ConfirmModal } from "../../../../components/ConfirmModal";
import { ModalCloseButton } from "../../../../components/ModalCloseButton";
import type {
  ManageableTeamResponse,
  DeletedTeamResponse,
  BulkTeamActionResponse,
} from "../../../../types/api";
import { tourAnchor } from "../../../tours/tourAnchors";
import "./ManageTeamsPanel.css";

// ModalCloseButton (see ModalCloseButton.tsx) does not declare `ref` in its own props, so passing
// one fails typecheck even though React 19 forwards it to the underlying <button> at runtime. This
// panel focuses the close button on open — see the effect below — so the ref is load-bearing.
// This local alias types that without touching the shared primitive.

type PanelTab = "teams" | "deleted";

interface ManageTeamsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after a delete or restore so the parent can refresh its team lists. */
  onTeamsChanged: () => void;
}

const plural = (count: number, singular: string, pluralForm = `${singular}s`) =>
  count === 1 ? singular : pluralForm;

interface TeamRowProps {
  team: ManageableTeamResponse;
  checked: boolean;
  disabled: boolean;
  onToggle: (teamId: string) => void;
}

const TeamRow = ({ team, checked, disabled, onToggle }: TeamRowProps) => {
  const inputId = `manage-team-${team.id}`;
  return (
    <li className="manage-teams-panel__row">
      <label className="manage-teams-panel__row-label" htmlFor={inputId}>
        <input
          id={inputId}
          type="checkbox"
          className="manage-teams-panel__checkbox"
          checked={checked}
          disabled={disabled}
          onChange={() => onToggle(team.id)}
          data-testid={`manage-team-checkbox-${team.id}`}
        />
        <span className="manage-teams-panel__row-details">
          <span className="manage-teams-panel__team-name">{team.name}</span>
          <span className="manage-teams-panel__team-meta">
            {team.playerCount === 0
              ? "No players"
              : `${team.playerCount} ${plural(team.playerCount, "player")}`}
            {" · "}
            Created {formatDateForDisplay(team.createdAt)}
          </span>
          {team.playerCount > 0 && (
            <span className="manage-teams-panel__warn-note">
              Archiving also removes this team's roster links and notes.
              Restoring brings them back.
            </span>
          )}
          {team.gameLinkCount > 0 && (
            <span className="manage-teams-panel__team-meta">
              Linked to {team.gameLinkCount} {plural(team.gameLinkCount, "game")}{" "}
              on the schedule
            </span>
          )}
        </span>
      </label>
    </li>
  );
};

interface DeletedTeamRowProps {
  team: DeletedTeamResponse;
  checked: boolean;
  disabled: boolean;
  onToggle: (teamId: string) => void;
}

const DeletedTeamRow = ({
  team,
  checked,
  disabled,
  onToggle,
}: DeletedTeamRowProps) => {
  const inputId = `deleted-team-${team.id}`;
  return (
    <li className="manage-teams-panel__row">
      <label className="manage-teams-panel__row-label" htmlFor={inputId}>
        <input
          id={inputId}
          type="checkbox"
          className="manage-teams-panel__checkbox"
          checked={checked}
          disabled={disabled}
          onChange={() => onToggle(team.id)}
          data-testid={`deleted-team-checkbox-${team.id}`}
        />
        <span className="manage-teams-panel__row-details">
          <span className="manage-teams-panel__team-name">{team.name}</span>
          <span className="manage-teams-panel__team-meta">
            {team.deletedAt
              ? `Archived ${formatDateForDisplay(team.deletedAt)}`
              : "Archived earlier"}
            {team.restorablePlayerCount > 0 &&
              ` · restores ${team.restorablePlayerCount} ${plural(
                team.restorablePlayerCount,
                "player"
              )}`}
          </span>
        </span>
      </label>
    </li>
  );
};

export const ManageTeamsPanel = ({
  isOpen,
  onClose,
  onTeamsChanged,
}: ManageTeamsPanelProps) => {
  const [activeTab, setActiveTab] = useState<PanelTab>("teams");
  const [teams, setTeams] = useState<ManageableTeamResponse[]>([]);
  const [deleted, setDeleted] = useState<DeletedTeamResponse[]>([]);
  const [emptyOnly, setEmptyOnly] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [result, setResult] = useState<
    (BulkTeamActionResponse & { action: PanelTab }) | null
  >(null);

  const closeButtonRef = useRef<HTMLButtonElement>(null);
  // The element focused before the panel opened, so focus can be restored on close.
  const previouslyFocused = useRef<HTMLElement | null>(null);

  // Both lists load together: an action on one tab changes the other, so fetching
  // them as a pair keeps the "Archived Teams" count from going stale.
  const loadTeams = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const [live, tombstoned] = await Promise.all([
        getManageableTeams(),
        getDeletedTeams(),
      ]);
      setTeams(live);
      setDeleted(tombstoned);
      setSelectedIds(new Set());
    } catch (error) {
      setLoadError(
        error instanceof Error ? error.message : "Failed to load teams"
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load fresh data each time the panel opens; reset transient state on close.
  useEffect(() => {
    if (isOpen) {
      setResult(null);
      setActionError(null);
      loadTeams();
    } else {
      setTeams([]);
      setDeleted([]);
      setSelectedIds(new Set());
      setActiveTab("teams");
      setEmptyOnly(false);
    }
  }, [isOpen, loadTeams]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen && !isSubmitting && !showConfirm) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, isSubmitting, showConfirm, onClose]);

  // Move focus into the panel on open and restore it to the trigger on close.
  useEffect(() => {
    if (isOpen) {
      previouslyFocused.current =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
      closeButtonRef.current?.focus();
    } else {
      previouslyFocused.current?.focus();
      previouslyFocused.current = null;
    }
  }, [isOpen]);

  const visibleTeams = useMemo(
    () => (emptyOnly ? teams.filter((t) => t.playerCount === 0) : teams),
    [teams, emptyOnly]
  );

  const rows: { id: string }[] = activeTab === "teams" ? visibleTeams : deleted;
  const allSelected = rows.length > 0 && selectedIds.size === rows.length;
  const selectedCount = selectedIds.size;

  // Players at risk across the current selection. Drives the stronger confirmation
  // copy so a coach never wipes twenty rosters behind a generic "Delete?" prompt.
  const selectedPlayerCount = useMemo(
    () =>
      activeTab === "teams"
        ? visibleTeams
            .filter((t) => selectedIds.has(t.id))
            .reduce((sum, t) => sum + t.playerCount, 0)
        : 0,
    [activeTab, visibleTeams, selectedIds]
  );

  const switchTab = (tab: PanelTab) => {
    if (tab === activeTab) return;
    setActiveTab(tab);
    setSelectedIds(new Set());
    setResult(null);
    setActionError(null);
  };

  const toggleTeam = (teamId: string) => {
    // A new selection supersedes any prior result banner.
    setResult(null);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(teamId)) {
        next.delete(teamId);
      } else {
        next.add(teamId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    setResult(null);
    setSelectedIds((prev) =>
      prev.size === rows.length ? new Set() : new Set(rows.map((r) => r.id))
    );
  };

  // Ticking a team and then hiding it behind "empty teams only" would otherwise leave
  // it queued for deletion invisibly. Drop anything the filter takes off screen.
  const toggleEmptyOnly = () => {
    setResult(null);
    const nextEmptyOnly = !emptyOnly;
    setEmptyOnly(nextEmptyOnly);
    if (nextEmptyOnly) {
      const stillVisible = new Set(
        teams.filter((t) => t.playerCount === 0).map((t) => t.id)
      );
      setSelectedIds(
        (prev) => new Set([...prev].filter((id) => stillVisible.has(id)))
      );
    }
  };

  const runAction = async () => {
    setShowConfirm(false);
    setIsSubmitting(true);
    setActionError(null);
    setResult(null);
    const action = activeTab;
    try {
      const ids = Array.from(selectedIds);
      const response =
        action === "teams"
          ? await bulkDeleteTeams(ids)
          : await restoreTeams(ids);
      setResult({ ...response, action });
      onTeamsChanged();
      // Re-fetch so both tabs reflect what actually happened server-side.
      await loadTeams();
    } catch (error) {
      setActionError(
        error instanceof Error
          ? error.message
          : action === "teams"
            ? "Failed to archive teams"
            : "Failed to restore teams"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isSubmitting) {
      onClose();
    }
  };

  const emptyStateMessage =
    activeTab === "deleted"
      ? "Nothing archived recently."
      : emptyOnly
        ? "No empty teams. You're all clean."
        : "No teams yet.";

  return (
    <>
      <div
        className={`manage-teams-panel-overlay ${
          isOpen ? "manage-teams-panel-overlay--open" : ""
        }`}
        onClick={handleOverlayClick}
      />
      <div
        className={`manage-teams-panel ${
          isOpen ? "manage-teams-panel--open" : ""
        }`}
        role="dialog"
        aria-modal="true"
        aria-label="Archive teams"
        aria-hidden={!isOpen}
        // When closed the panel stays mounted (for the slide transition), so
        // `inert` removes its controls from tab order and the a11y tree.
        inert={!isOpen}
      >
        <div className="manage-teams-panel__header">
          <div className="manage-teams-panel__heading">
            <h2 className="manage-teams-panel__title">Archive teams</h2>
            <p className="manage-teams-panel__subtitle">
              Select teams to archive. You can bring them back any time from
              Archived Teams.
            </p>
          </div>
          <ModalCloseButton
            ref={closeButtonRef}
            onClick={onClose}
            disabled={isSubmitting}
            data-testid="manage-teams-close"
            {...tourAnchor("players.teams.panel.close")}
          />
        </div>

        <div className="manage-teams-panel__tabs" role="tablist">
          <button
            type="button"
            role="tab"
            id="manage-teams-tab-teams"
            aria-selected={activeTab === "teams"}
            aria-controls="manage-teams-panel-body"
            className={`manage-teams-panel__tab ${
              activeTab === "teams" ? "manage-teams-panel__tab--active" : ""
            }`}
            onClick={() => switchTab("teams")}
            disabled={isSubmitting}
            data-testid="manage-teams-tab-teams"
          >
            Teams ({teams.length})
          </button>
          <button
            type="button"
            role="tab"
            id="manage-teams-tab-deleted"
            aria-selected={activeTab === "deleted"}
            aria-controls="manage-teams-panel-body"
            className={`manage-teams-panel__tab ${
              activeTab === "deleted" ? "manage-teams-panel__tab--active" : ""
            }`}
            onClick={() => switchTab("deleted")}
            disabled={isSubmitting}
            data-testid="manage-teams-tab-deleted"
          >
            Archived Teams ({deleted.length})
          </button>
        </div>

        <div
          className="manage-teams-panel__body"
          id="manage-teams-panel-body"
          role="tabpanel"
          aria-labelledby={`manage-teams-tab-${activeTab}`}
          {...tourAnchor("players.teams.panel")}
        >
          {result && (
            <div
              className="manage-teams-panel__result"
              role="status"
              data-testid="manage-teams-result"
            >
              {result.action === "teams"
                ? `Archived ${result.affectedCount} ${plural(
                    result.affectedCount,
                    "team"
                  )}.`
                : `Restored ${result.affectedCount} ${plural(
                    result.affectedCount,
                    "team"
                  )}.`}
              {result.skippedIds.length > 0 && (
                <span className="manage-teams-panel__result-skipped">
                  {result.skippedIds.length}{" "}
                  {plural(result.skippedIds.length, "team was", "teams were")}{" "}
                  skipped. Someone else may have changed them already.
                </span>
              )}
            </div>
          )}

          {actionError && (
            <div className="manage-teams-panel__error" role="alert">
              {actionError}
            </div>
          )}

          {isLoading ? (
            <div className="manage-teams-panel__loading">Loading teams...</div>
          ) : loadError ? (
            <div className="manage-teams-panel__error" role="alert">
              {loadError}
            </div>
          ) : rows.length === 0 ? (
            <div
              className="manage-teams-panel__empty"
              data-testid="manage-teams-empty"
            >
              {emptyStateMessage}
            </div>
          ) : (
            <>
              <div className="manage-teams-panel__controls">
                <label className="manage-teams-panel__select-all">
                  <input
                    type="checkbox"
                    className="manage-teams-panel__checkbox"
                    checked={allSelected}
                    disabled={isSubmitting}
                    onChange={toggleSelectAll}
                    data-testid="manage-teams-select-all"
                  />
                  <span>Select all ({rows.length})</span>
                </label>
                {activeTab === "teams" && (
                  <label className="manage-teams-panel__filter">
                    <input
                      type="checkbox"
                      className="manage-teams-panel__checkbox"
                      checked={emptyOnly}
                      disabled={isSubmitting}
                      onChange={toggleEmptyOnly}
                      data-testid="manage-teams-empty-only"
                    />
                    <span>Empty teams only</span>
                  </label>
                )}
              </div>
              <ul className="manage-teams-panel__list">
                {activeTab === "teams"
                  ? visibleTeams.map((team) => (
                      <TeamRow
                        key={team.id}
                        team={team}
                        checked={selectedIds.has(team.id)}
                        disabled={isSubmitting}
                        onToggle={toggleTeam}
                      />
                    ))
                  : deleted.map((team) => (
                      <DeletedTeamRow
                        key={team.id}
                        team={team}
                        checked={selectedIds.has(team.id)}
                        disabled={isSubmitting}
                        onToggle={toggleTeam}
                      />
                    ))}
              </ul>
            </>
          )}
        </div>

        {rows.length > 0 && (
          <div className="manage-teams-panel__footer">
            <Button
              variant="primary"
              fullWidth
              disabled={selectedCount === 0 || isSubmitting}
              onClick={() => setShowConfirm(true)}
              data-testid={
                activeTab === "teams"
                  ? "manage-teams-delete"
                  : "manage-teams-restore"
              }
            >
              {activeTab === "teams"
                ? isSubmitting
                  ? "Archiving..."
                  : `Archive selected${selectedCount > 0 ? ` (${selectedCount})` : ""}`
                : isSubmitting
                  ? "Restoring..."
                  : `Restore selected${selectedCount > 0 ? ` (${selectedCount})` : ""}`}
            </Button>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={runAction}
        busy={isSubmitting}
        title={
          activeTab === "teams"
            ? `Archive ${selectedCount} ${plural(selectedCount, "team")}?`
            : `Restore ${selectedCount} ${plural(selectedCount, "team")}?`
        }
        message={
          activeTab === "teams"
            ? selectedPlayerCount > 0
              ? `This takes ${selectedPlayerCount} ${plural(
                  selectedPlayerCount,
                  "player"
                )} off ${plural(
                  selectedCount,
                  "that roster",
                  "those rosters"
                )}, along with the teams' notes. The players themselves are kept, and you can undo this from Archived Teams.`
              : `${plural(
                  selectedCount,
                  "This team has",
                  "These teams have"
                )} no players. You can undo this from Archived Teams.`
            : `${plural(
                selectedCount,
                "This team",
                "These teams"
              )} will come back with the roster links and notes removed when they were archived.`
        }
        confirmText={
          activeTab === "teams"
            ? `Archive ${selectedCount}`
            : `Restore ${selectedCount}`
        }
        cancelText="Cancel"
      />
    </>
  );
};
